import { Box, Button, Card, CardContent, Typography, Alert } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { colors } from "../../../ui/colors";

import { patchDeviceTracker, unLinkDeviceTracker } from "../../../../utils";
import { DeviceStatusWithMap } from "../../../DeviceTracker/DeviceStatusWithMap";
import { DeviceTracker } from "../../../DeviceTracker/DeviceTracker";
import { DEVICE_MODES, DEVICE_STATUS } from "../../../DeviceTracker/helpers/DeviceModesConst";
import { useDeviceSocket, useRefreshUntilLinked } from "../../../DeviceTracker/hooks/useDeviceSocket";
import { useDeviceTracker } from "../../../DeviceTracker/hooks/useDeviceTracker";
import { BaseModalComponent } from "../../../common/BaseModalComponent";
import { useModals } from "../../../../context/ModalContext";
import { publishWorkforceDevice } from "../../../DeviceTracker/devicePayload";
// ─── Mode mapping ────────────────────────────────────────────────────────────

const UI_TO_API_MODE = {
  eco: "eco_mode",
  realtime: "real_time",
};

const API_TO_UI_MODE = Object.fromEntries(Object.entries(UI_TO_API_MODE).map(([ui, api]) => [api, ui]));

const PAIR_MODAL_KEY = "Workforce-Pair-Device";

// ─── Component ────────────────────────────────────────────────────────────────

export const DeviceTrackerCard = ({ location, coverageRadius, address, workforce }) => {
  const { companies } = useSelector((state) => state.userState);
  const { getDeviceById, lastConexion, setLastConexion, device, setDevice, dropDevice } = useDeviceTracker();

  const [selectedMode, setSelectedMode] = useState(DEVICE_MODES.BALANCED);
  const [waitingForLocation, setWaitingForLocation] = useState(false);
  const [justPaired, setJustPaired] = useState(false);
  const previousStatus = useRef(device?.status);
  const { modals, closeModal } = useModals();

  const companyId = companies?.[0]?.companyId;

  useEffect(() => {
    if (workforce?.uid) publishWorkforceDevice(workforce.uid, device);
  }, [workforce?.uid, device]);

  const refreshDevice = useCallback(() => {
    if (!workforce?.uid) return Promise.resolve();

    return getDeviceById(workforce.uid, true);
  }, [workforce?.uid, getDeviceById]);

  const handleDeviceLinked = useCallback(
    (linkedDevice) => {
      setDevice((current) => ({
        ...(current && typeof current === "object" ? current : {}),
        ...(linkedDevice && typeof linkedDevice === "object" ? linkedDevice : {}),
        status: DEVICE_STATUS.LINKED,
        uid: linkedDevice?.deviceUid || linkedDevice?.uid || current?.uid,
      }));
      setWaitingForLocation(true);
      refreshDevice();
    },
    [setDevice, refreshDevice],
  );

  const handleDeviceRecord = useCallback(async (payload) => {
    try {
      const data = typeof payload === "string" ? JSON.parse(payload) : payload;
      if (data?.kind === "position" && data.gps) {
        const lat = Number(data.gps.latitude);
        const lng = Number(data.gps.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setLastConexion({ lat, lng });
          setWaitingForLocation(false);
        }
        return;
      }
    } catch (error) {
      console.error("[DeviceTrackerCard] Could not read the live position", error);
    }

    await refreshDevice();
    setWaitingForLocation(false);
  }, [refreshDevice, setLastConexion]);

  useDeviceSocket({
    workforceUid: workforce?.uid,
    companyId,
    onDisconnect: refreshDevice,
    onLink: handleDeviceLinked,
    onRecord: handleDeviceRecord,
  });

  useRefreshUntilLinked(workforce?.uid, device?.status, refreshDevice);

  useEffect(() => {
    if (device?.status !== DEVICE_STATUS.LINKED || !workforce?.uid) return undefined;
    const timer = setInterval(() => {
      refreshDevice();
    }, 8000);
    return () => clearInterval(timer);
  }, [device?.status, workforce?.uid, refreshDevice]);

  useEffect(() => {
    const previous = previousStatus.current;
    previousStatus.current = device?.status;
    if (device?.status !== DEVICE_STATUS.LINKED || !previous || previous === DEVICE_STATUS.LINKED) return;

    setJustPaired(true);
    (modals || []).filter((modal) => modal.key === PAIR_MODAL_KEY).forEach((modal) => closeModal(modal.id));
  }, [device?.status, modals, closeModal]);

  useEffect(() => {
    refreshDevice();
  }, [refreshDevice]);

  const handleModeChange = async (event) => {
    const newMode = event.target.value;

    setSelectedMode(newMode);

    if (!device?.uid) return;

    const modeForApi = UI_TO_API_MODE[newMode] ?? newMode;

    try {
      const token = sessionStorage.getItem("token");

      const updatedDevice = await patchDeviceTracker(token, device.uid, {
        mode: modeForApi,
      });

      setDevice(
        updatedDevice ??
          ((prev) => ({
            ...prev,
            mode: modeForApi,
          })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleUnLinkDevice = async () => {
    setJustPaired(false);
    try {
      const token = sessionStorage.getItem("token");

      dropDevice(device.uid);
      await unLinkDeviceTracker(token, workforce?.uid, device.uid);
      publishWorkforceDevice(workforce?.uid, null);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!device?.mode) return;

    const uiMode = API_TO_UI_MODE[device.mode] ?? device.mode;

    if (uiMode !== selectedMode) {
      setSelectedMode(uiMode);
    }
  }, [device?.mode, selectedMode]);

  useEffect(() => {
    if (!waitingForLocation) return;

    const timeoutId = setTimeout(() => {
      setWaitingForLocation(false);
    }, 10000);

    return () => clearTimeout(timeoutId);
  }, [waitingForLocation]);

  const isDeviceLinked = device?.status === DEVICE_STATUS.LINKED;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "none",
        height: "100%",
      }}
    >
      <CardContent
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ ml: 2 }}>
            <Typography
              sx={{
                fontSize: isDeviceLinked ? 20 : 18,
                fontWeight: 600,
                color: isDeviceLinked ? colors.gray[900] : colors.gray[400],
              }}
            >
              {isDeviceLinked
                ? `Where is my ${workforce?.workforceRole}?`
                : `Could not locate ${workforce?.firstName || ""} ${workforce?.lastName || ""}, their device is not paired`}
            </Typography>

            {device?.shortUid && (
              <Typography
                variant="body2"
                sx={{
                  color: colors.gray[400],
                  fontSize: "0.85rem",
                  mt: 0.5,
                  fontWeight: 500,
                }}
              >
                Device ID:{" "}
                <span
                  style={{
                    fontFamily: "monospace",
                    fontWeight: 600,
                  }}
                >
                  {device.shortUid}
                </span>
              </Typography>
            )}
          </Box>
        </Box>

        {justPaired && (
          <Alert
            severity="success"
            action={
              <Button color="inherit" size="small" onClick={() => setJustPaired(false)}>
                OK
              </Button>
            }
            sx={{ mx: 2, mt: 1.5 }}
          >
            Device paired. You can see it on the map.
          </Alert>
        )}

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {isDeviceLinked ? (
              <DeviceStatusWithMap
                workforce={workforce}
                device={device}
                lastConexion={lastConexion}
                selectedMode={selectedMode}
                handleUnLinkDevice={handleUnLinkDevice}
                waitingForLocation={waitingForLocation}
              />
            ) : (
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "260px",
                  gap: 1.5,
                }}
              >
                <BaseModalComponent
                  modalKey="Workforce-Pair-Device"
                  modalLabel="Pair Device"
                  elementTrigger={
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: "#f97316",
                        "&:hover": {
                          bgcolor: "#ea580c",
                        },
                        fontWeight: 600,
                        textTransform: "none",
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                      }}
                    >
                      Pair Device
                    </Button>
                  }
                  defaultSizes={{
                    width: "95%",
                    height: 525,
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: 1, md: 3 },
                      height: "100%",
                      overflowY: "auto",
                    }}
                  >
                    <DeviceTracker
                      getDeviceById={getDeviceById}
                      workforce={workforce}
                      deviceTrackerUid={device?.uid}
                      device={device}
                      setDevice={setDevice}
                    />
                  </Box>
                </BaseModalComponent>

                <Typography
                  variant="body2"
                  color={colors.gray[400]}
                  sx={{
                    textAlign: "center",
                    mt: 1,
                    fontSize: "0.7rem",
                  }}
                >
                  Click the button to start tracking this workforce
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
